import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {useApolloClient} from "@apollo/client";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconButton} from "@mui/material";
import {useEffect, useState} from "react";
import * as React from "react";
import {useDispatch} from "react-redux";
import Loading from "../breadcrumbs/Loading";
import {PromptDialog} from "@dvrt/graphql-widgets";
import {
    DeleteVoicemailMutation,
    DeleteVoicemailMutationVariables,
    DeleteVoicemailDocument,
    MarkVoicemailMutation,
    MarkVoicemailMutationVariables,
    MarkVoicemailDocument,
} from "./mutations.generated";
import {VoicemailMessageQuery, VoicemailMessageQueryVariables, VoicemailMessageDocument} from "./queries.generated";
import {VOICEMAIL_FETCH_ERRORS} from "@/utils/translations";

type Props = {
    voicemailId: number;
    listened: boolean;
    extensionId: number;
};

/**
 * Function to generate source that can be used to play voicemail
 *
 * @param data base64 encoded string
 * @returns source for audio element or null if data can not be sliced
 */
function generateAudioSource(data: string): string | null {
    try {
        data = data.slice(2, -1);
        return `data:audio/ogg;base64,${data.replace(/\\n/g, "")}`;
    } catch {
        return null;
    }
}

/**
 * Button to play voicemail
 *
 * @param voicemail id of voicemail
 * @param listened whether the voicemail has been listened to
 * @param extensionId id of the extension
 * @returns buttons to play and delete voicemail
 */
export default function VoicemailButton({voicemailId, listened, extensionId}: Props) {
    const [deleted, setDeleted] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    // Animatons when the deletion finished to make it less jarring
    const styleAfterDeletion = {
        pointerEvents: deleted ? "none" : null,
        maxHeight: deleted ? "0px" : "100px",
        opacity: deleted ? "0" : "1",
    } as React.CSSProperties;

    const client = useApolloClient();
    const dispatch = useDispatch();

    const deleteVoicemail = async () => {
        setDeleting(true);
        const res = await client.mutate<DeleteVoicemailMutation, DeleteVoicemailMutationVariables>({
            mutation: DeleteVoicemailDocument,
            variables: {
                voicemailId: voicemailId,
                userId: extensionId,
            },
        });

        setDeleting(false);
        if (res.errors || !res.data.deleteVoicemailMessage || res.data.deleteVoicemailMessage.errors.length > 0) {
            dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.delete, "error"));
            return;
        }

        dispatch(
            showNotification(defineMessage({id: "voicemail.deleted", defaultMessage: "Voicemail deleted"}), "success"),
        );

        setDeleted(true);
    };

    return (
        <div style={{...ContainerStyle, ...styleAfterDeletion}}>
            <AudioPlayer
                voicemailId={voicemailId}
                listened={listened}
                extensionId={extensionId}
                deleting={deleting}
                deleted={deleted}
            />
            <DeleteButton
                voicemail={voicemailId}
                deleting={deleting}
                onDelete={() => {
                    deleteVoicemail().then();
                }}
            />
        </div>
    );
}

/**
 * Button to play the voicemail
 *
 * @param voicemailId id of the voicemail to be fetched and played
 * @param listened whether the voicemail has been listened to
 * @param deleting whether the voicemail is being deleted
 * @param deleted whether the voicemail has been deleted
 * @param extensionId id of the extension
 * @returns audio element to play voicemail
 */
const AudioPlayer = ({
    voicemailId,
    listened,
    deleting,
    deleted,
    extensionId,
}: Props & {deleting: boolean; deleted: boolean}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [source, setSource] = useState<string | null>(null);
    const audioRef = React.useRef<HTMLAudioElement>(null);

    useEffect(() => {
        if (deleted) {
            // Not removing it, because it will just disappear while the animation is still running
            audioRef.current?.pause();
        }
    }, [deleted]);

    const client = useApolloClient();
    const dispatch = useDispatch();

    /**
     * Function to fetch the voicemail message
     */
    const fetchVoicemail = async () => {
        setLoading(true);
        try {
            const {data: messageData, errors: messageErrors} = await client.query<
                VoicemailMessageQuery,
                VoicemailMessageQueryVariables
            >({
                query: VoicemailMessageDocument,
                variables: {
                    voicemailId: voicemailId,
                    userId: extensionId,
                },
            });

            const {data: markData, errors: markErrors} = await client.mutate<
                MarkVoicemailMutation,
                MarkVoicemailMutationVariables
            >({
                mutation: MarkVoicemailDocument,
                variables: {
                    voicemailId: voicemailId,
                    userId: extensionId,
                },
            });

            if (messageErrors) {
                dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.voicemail_api, "error"));
                return;
            } else if (markErrors || markData.markVoicemailMessage.errors.length > 0) {
                dispatch(
                    showNotification(
                        defineMessage({
                            id: "voicemail.fetcherror.listen",
                            defaultMessage: "Could not mark voicemail as listened",
                        }),
                        "error",
                    ),
                );
            }

            const generatedSource = generateAudioSource(messageData.voicemailMessage);
            if (!generatedSource) {
                dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.decoding, "error"));
                return;
            }

            // Check if the voicemail can be played
            // If the source is invalid, the audio element will not play
            const audio = new Audio(generatedSource);
            let error = false;
            try {
                await audio.play();
                audio.pause();
            } catch {
                dispatch(
                    showNotification(
                        defineMessage({id: "voicemail.playerror", defaultMessage: "Could not play voicemail"}),
                        "error",
                    ),
                );
                error = true;
            } finally {
                audio.remove();
            }

            // If the source can be played, only then set it
            if (!error) setSource(generatedSource);
        } catch {
            dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.voicemail_api, "error"));
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loading />;

    if (!source)
        return (
            <IconButton
                onClick={fetchVoicemail}
                disabled={deleting}
                color={listened ? "default" : "primary"}
                size="large"
            >
                <FontAwesomeIcon icon="play" />
            </IconButton>
        );

    return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio controls src={source} autoPlay ref={audioRef} />
    );
};

/**
 * Button to delete voicemail
 *
 * @param voicemail id of the voicemail to be deleted
 * @param extensionId id of the extension
 * @param setDeleted function to set deleted state
 * @param toggleDeleting function to toggle deleting state
 * @returns button to delete voicemail
 */
const DeleteButton = ({deleting, onDelete}: {voicemail: number; deleting: boolean; onDelete: () => void}) => {
    const [prompting, setPrompting] = useState<boolean>(false);

    const handleChoice = (choice: boolean) => {
        if (choice) onDelete();
        setPrompting(false);
    };

    return (
        <>
            <IconButton onClick={() => setPrompting(true)} disabled={deleting} size="large">
                <FontAwesomeIcon icon="trash" color="red" style={{transition: "color 0.3s ease-in-out"}} />
            </IconButton>
            <PromptDialog
                open={prompting}
                title={defineMessage({id: "voicemail.prompt.title", defaultMessage: "Delete voicemail"})}
                message={defineMessage({
                    id: "voicemail.prompt.message",
                    defaultMessage: "Are you sure you want to delete this voicemail?",
                })}
                choice={handleChoice}
            />
        </>
    );
};

const ContainerStyle = {
    display: "flex",
    alignItems: "center",
    overflow: "clip",
    transition: "max-height 0.7s ease-out, opacity 0.3s ease-in-out",
} as React.CSSProperties;
